import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useUserModulesPageQuery } from '@entities/Module';
import { EmptyModulesState } from '@features/Dashboard';
import type { TrainingModulePlan } from '@features/Session';
import { buildUnitPath } from '@shared/config';
import { cn, useDelayedFlag } from '@shared/lib';
import { Pagination, PlainField, PlayIcon, SegPills, type SegPillOption } from '@shared/ui';
import type { ModuleSummary } from '@shared/types';
import { lineStatus, trainPath, useTrainingPlans } from './moduleTraining';

type Filter = 'all' | 'deleted';

const PAGE_SIZE = 20;
const FILTER_TO_STATUS = { all: 'available', deleted: 'deleted' } as const;
/** С какого числа модулей нужен поиск: меньше — список виден целиком (как на «Тренировке»). */
const SEARCH_FROM = 7;
/** Есть ли удалённые: фильтр «Все / Удалённые» нужен, только когда есть что открыть. */
const DELETED_PROBE = { page: 1, size: 1, status: 'deleted' } as const;

interface ModuleLineProps {
  module: ModuleSummary;
  index: number;
  plan: TrainingModulePlan | undefined;
  onOpen: () => void;
  /** ▶ — тренировка «На сегодня» этого модуля; нет готовых слов или модуль удалён — кнопки нет. */
  onTrain?: () => void;
  /** Выбран: справа — его слова; подсветка — как в наброске B (рамка и тёплый фон, ▶ акцентом). */
  selected?: boolean;
  /** Сколько добавленных сегодня и не тренированных карточек в модуле: «+N» и «N новых · сегодня». */
  fresh?: number;
  /** Нажатие выбирает модуль, а не открывает его страницу. */
  selectable?: boolean;
}

/** Строка модуля: нажатие — страница модуля (на «Словах и модулях» — выбор: справа его слова), ▶ справа — сразу
 *  тренировка (решения владельца 2026-10-23, 2026-10-28). */
function ModuleLine({ module: m, index, plan, onOpen, onTrain, selected = false, fresh = 0, selectable = false }: ModuleLineProps) {
  const { t } = useTranslation();
  const status = !m.deleted && plan ? lineStatus(plan, t) : null;
  return (
    // Рамка видна всегда (серое на тёмном — с рамкой); наведение — только мышью, иначе на iOS залипает.
    <div
      className={cn(
        'flex items-center gap-2 rounded-[18px] border border-solid pr-3 transition-colors',
        selected
          ? 'border-accent/60 bg-card bg-[linear-gradient(90deg,rgba(243,106,28,0.12),rgba(243,106,28,0)_72%)]'
          : 'border-line bg-card [@media(hover:hover)]:hover:border-[#3a3a40]',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-pressed={selectable ? selected : undefined}
        aria-label={selectable ? t('words.mine.select', { name: m.title }) : undefined}
        className="flex min-h-16 min-w-0 flex-1 items-center gap-4 rounded-[18px] py-3 pl-4 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent nav:pl-5"
      >
        <span aria-hidden className="hidden w-7 flex-none font-serif text-[20px] leading-6 text-muted sm:block">
          {String(index + 1).padStart(2, '0')}
        </span>
        <span className="min-w-0 flex-1">
          {/* Имя — во всю строку (рядом только «+N»), справа «Учить»; во второй строке — что повторить. */}
          <span className="flex items-baseline gap-2">
            <span className="min-w-0 flex-1 truncate text-[15px] font-medium leading-5 text-fg">{m.title}</span>
            {fresh > 0 && (
              <span className="inline-flex h-5 flex-none items-center self-center rounded-pill bg-accent px-2 text-[11px] font-bold text-fg-inv">
                +{fresh}
              </span>
            )}
          </span>
          <span className="mt-1 flex min-w-0 items-center gap-2 text-[13px] leading-[18px]">
            {fresh > 0 && !m.deleted ? (
              <>
                <span aria-hidden className="h-2 w-2 flex-none rounded-full bg-accent shadow-[0_0_0_3px_rgba(243,106,28,0.25)]" />
                <span className="min-w-0 truncate font-semibold text-accent-hi">{t('words.mine.fresh', { count: fresh })}</span>
              </>
            ) : (
              status && (
                <>
                  <span aria-hidden className={cn('h-2 w-2 flex-none rounded-full', status.repeat ? 'bg-accent' : 'bg-white/[0.16]')} />
                  <span className={cn('min-w-0 truncate', status.repeat ? 'text-accent' : 'text-muted')}>{status.text}</span>
                </>
              )
            )}
            {/* Сводка уже называет слова — число карточек только без неё (удалённый, сводка ещё не пришла). */}
            {!(fresh > 0 && !m.deleted) && !status && (
              <span className="flex-none text-[12px] text-muted">
                {m.deleted ? t('dashboard.modules.chips.deleted') : t('training.cardCount', { count: m.words })}
              </span>
            )}
          </span>
        </span>
      </button>
      {onTrain && (
        <button
          type="button"
          onClick={onTrain}
          aria-label={t('words.mine.train', { name: m.title })}
          title={t('words.mine.trainTitle')}
          className={cn(
            'inline-flex h-9 flex-none items-center gap-1.5 rounded-pill px-3.5 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            selected || fresh > 0 ? 'bg-accent text-fg-inv hover:bg-accent-hi' : 'bg-elev text-fg hover:bg-accent hover:text-fg-inv',
          )}
        >
          <PlayIcon className="h-3 w-3" />
          {t('words.mine.learn')}
        </button>
      )}
    </div>
  );
}

/**
 * «Мои модули» на «Словах и модулях» (2026-10-23, бывшая страница «Модули»): у каждого модуля — сколько карточек,
 * что повторить (сводка `/sessions/cram/overview`, общий кэш с «Тренировкой») и ▶ — тренировка «На сегодня». Поиск —
 * с 7 модулей, «Все / Удалённые» — когда есть удалённые: новичку видно только список. `/words?filter=deleted`
 * (бывшее `/units?filter=deleted`) открывает «Удалённые».
 */
export function MyModules({
  onCreateModule,
  selectedId = null,
  onSelect,
  fresh = {},
}: {
  onCreateModule: () => void;
  /** «Слова и модули» на компьютере: модуль выбирается, справа — его слова (владелец 2026-10-28). */
  selectedId?: string | null;
  onSelect?: (module: ModuleSummary) => void;
  /** Добавленные сегодня и не тренированные карточки по модулям — «+N». */
  fresh?: Record<string, number>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<Filter>(() => (searchParams.get('filter') === 'deleted' ? 'deleted' : 'all'));
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [filter, search]);

  const queryArgs = useMemo(
    () => ({ page, size: PAGE_SIZE, search: search || undefined, status: FILTER_TO_STATUS[filter] }),
    [filter, page, search],
  );
  const { data, isPending } = useUserModulesPageQuery(queryArgs);
  const deletedProbe = useUserModulesPageQuery(DELETED_PROBE);
  const plans = useTrainingPlans();
  const items = data?.items ?? [];
  const visible = filter === 'all' ? items.filter((m) => !m.deleted) : items;
  const showSkeleton = useDelayedFlag(isPending, 180);
  const showFilter = filter === 'deleted' || (deletedProbe.data?.total ?? 0) > 0;
  const showSearch = searchInput !== '' || (data?.total ?? 0) >= SEARCH_FROM;

  const filterOptions: SegPillOption<Filter>[] = [
    { key: 'all', label: t('units.filters.all') },
    { key: 'deleted', label: t('units.filters.deleted') },
  ];

  const noModulesAtAll = !isPending && data?.total === 0 && !search && filter === 'all';

  const train = (plan: TrainingModulePlan) => navigate(trainPath(plan, 'words'), { state: { fromWords: true } });

  return (
    <div>
      {(showSearch || showFilter) && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          {showSearch && (
            <PlainField
              value={searchInput}
              onChange={setSearchInput}
              placeholder={t('units.searchPlaceholder')}
              ariaLabel={t('units.searchPlaceholder')}
              className="w-full max-w-[420px] rounded-pill border border-line bg-card px-5 py-2.5 text-[14px] text-fg outline-none transition-colors focus:border-accent"
            />
          )}
          {showFilter && <SegPills options={filterOptions} value={filter} onChange={setFilter} />}
        </div>
      )}

      <AnimatePresence initial={false}>
        {filter === 'deleted' && !isPending && (
          <motion.div
            key="deleted-notice"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.22, ease: [0.2, 0.9, 0.2, 1] } }}
            exit={{ opacity: 0, y: -4, transition: { duration: 0.16, ease: 'easeOut' } }}
            className="mb-4 flex items-start gap-2.5 rounded-card border border-danger/25 bg-danger/[0.06] px-5 py-3.5 text-[14px] leading-relaxed text-muted"
          >
            <span aria-hidden className="mt-px text-danger">●</span>
            <span>{t('units.deletedNotice')}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative">
        <AnimatePresence mode="wait" initial={false}>
          {isPending ? (
            showSkeleton ? (
              <motion.div
                key="skeleton"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                className="flex flex-col gap-2"
              >
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-[72px] animate-skeleton rounded-[18px] bg-card" />
                ))}
              </motion.div>
            ) : (
              <div key="placeholder" style={{ minHeight: 4 * 72 + 3 * 8 }} />
            )
          ) : noModulesAtAll ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.2, 0.9, 0.2, 1] }}
            >
              {/* «Создать модуль» — как «+ Новый модуль» в строке создания: модуль выбирается в ней (ТЗ главной § 9);
                  «Открыть удалённые» переключает фильтр здесь же (ТЗ демо 1.4). */}
              <EmptyModulesState onCreateModule={onCreateModule} onOpenDeleted={() => setFilter('deleted')} emailInDock />
            </motion.div>
          ) : !data ? (
            <div key="no-data" style={{ minHeight: 4 * 72 + 3 * 8 }} />
          ) : visible.length > 0 ? (
            <motion.div
              key={`list-${filter}-${page}-${search}`}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, y: -4, transition: { duration: 0.16, ease: 'easeOut' } }}
              variants={{
                hidden: { opacity: 1 },
                visible: { opacity: 1, transition: { staggerChildren: 0.04, delayChildren: 0.02 } },
              }}
              className="flex flex-col gap-2"
            >
              {visible.map((m, i) => {
                const plan = plans.get(m.id);
                return (
                  <motion.div
                    key={m.id}
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      visible: { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.2, 0.9, 0.2, 1] } },
                    }}
                  >
                    <ModuleLine
                      module={m}
                      index={(page - 1) * PAGE_SIZE + i}
                      plan={plan}
                      selectable={!!onSelect && !m.deleted}
                      selected={!m.deleted && m.id === selectedId}
                      fresh={fresh[m.id] ?? 0}
                      // Удалённый модуль не выбирается: его слов нет в «Словах», а вернуть его можно на его странице.
                      onOpen={() => (onSelect && !m.deleted ? onSelect(m) : navigate(buildUnitPath(m.id)))}
                      onTrain={plan && plan.ready > 0 && !m.deleted ? () => train(plan) : undefined}
                    />
                  </motion.div>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              key="no-match"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="py-10 text-center text-muted"
            >
              {t('units.empty')}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onChange={setPage} />}
    </div>
  );
}
